// TODO: Remove this file when the /brands API endpoint is ready.
// Simulates server-side filter, sort, and paging against static mock data.
import { Injectable, inject } from '@angular/core';
import {
    ApiCollectionMetaData,
    ApiCollectionResponse,
    ApiSortDirection,
    SearchState,
} from '@cia-front-end-apps/shared/api-interaction';
import { NEVER, Observable, defer, of, throwError } from 'rxjs';
import { Brand } from '../models/brand';
import { EXISTING_BRANDS, MOCK_BRANDS, brandsForScenario } from './brand.mock-data';
import { BrandDemoScenarioService } from './brand-demo-scenario.service';

@Injectable()
export class BrandResourceDev {
    private readonly demoScenarioService = inject(BrandDemoScenarioService);

    /**
     * TODO: Remove when the /brands/:id API endpoint is ready.
     * Simulates a brand detail fetch against static mock data.
     */
    get(brandId: string): Observable<Brand> {
        return defer(() => {
            const scenario = this.demoScenarioService.scenario();

            if (scenario === 'error') {
                return throwError(() => new Error('Mock error scenario'));
            }
            if (scenario === 'loading') {
                return NEVER;
            }

            const id = parseInt(brandId, 10);
            const brand = [...MOCK_BRANDS, ...EXISTING_BRANDS].find(b => b.brandId === id);
            return brand
                ? of(brand)
                : throwError(() => new Error(`Brand ${brandId} not found in mock data`));
        });
    }

    /**
     * defer() reads the current scenario at subscription time so that invalidating
     * the query cache and refetching picks up the latest scenario automatically.
     */
    getAll(searchState: SearchState): Observable<ApiCollectionResponse<Brand>> {
        return defer(() => {
            const scenario = this.demoScenarioService.scenario();

            if (scenario === 'error') {
                return throwError(() => new Error('Mock error scenario'));
            }
            if (scenario === 'loading') {
                return NEVER;
            }

            const allItems = brandsForScenario(scenario).filter(
                b => !this.demoScenarioService.removedIds().has(b.brandId),
            );
            const filtered = this.applyFilter(allItems, searchState.search);
            const sorted = this.applySort(filtered, searchState.sortBy, searchState.sortDirection);
            const { page, total } = this.applyPaging(sorted, searchState.pageIndex, searchState.pageSize);

            return of(new ApiCollectionResponse<Brand>({
                items: page,
                meta: new ApiCollectionMetaData({ itemCount: total }),
            }));
        });
    }

    searchParentBrands(term: string, pageSize = 15): Observable<Brand[]> {
        return defer(() => {
            const scenario = this.demoScenarioService.scenario();
            if (scenario === 'error') { return throwError(() => new Error('Mock error scenario')); }
            if (scenario === 'loading') { return NEVER; }
            const lower = term.toLowerCase();
            return of(EXISTING_BRANDS.filter(b =>
                b.brandName.toLowerCase().includes(lower) ||
                (b.upcs ?? []).some(u => u.upc.startsWith(lower)),
            ).slice(0, pageSize));
        });
    }

    rollUp(_newBrandId: number, _parentBrandId: number): Observable<void> {
        return defer(() => {
            const scenario = this.demoScenarioService.scenario();
            if (scenario === 'error') { return throwError(() => new Error('Mock error scenario')); }
            if (scenario === 'loading') { return NEVER; }
            return of(undefined as void);
        });
    }

    /**
     * TODO: Remove when the /brands API endpoint is ready.
     * Mock create — the brand is already removed from the queue optimistically.
     */
    create(brand: Brand): Observable<Brand> {
        return defer(() => {
            const scenario = this.demoScenarioService.scenario();
            if (scenario === 'error') {
                return throwError(() => new Error('Mock error scenario'));
            }
            if (scenario === 'loading') {
                return NEVER;
            }
            return of(brand);
        });
    }

    private applyFilter(items: Brand[], search?: string): Brand[] {
        if (!search) { return items; }
        const term = search.toLowerCase();
        return items.filter(b => b.brandName.toLowerCase().includes(term));
    }

    private applySort(items: Brand[], sortBy?: string, direction?: ApiSortDirection): Brand[] {
        const dir = direction === 'asc' ? 1 : -1; // default desc (newest first)
        return [...items].sort((a, b) => {
            if (sortBy === 'upcCount') {
                const diff = a.upcCount - b.upcCount;
                // Tiebreaker: stable secondary sort by brandId so position is fully deterministic.
                return dir * (diff !== 0 ? diff : a.brandId - b.brandId);
            }
            // default: sort by createdDate; tiebreaker: brandId
            const diff = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
            return dir * (diff !== 0 ? diff : a.brandId - b.brandId);
        });
    }

    private applyPaging(
        items: Brand[],
        pageIndex = 0,
        pageSize = 25,
    ): { page: Brand[]; total: number } {
        const total = items.length;
        const start = pageIndex * pageSize;
        return { page: items.slice(start, start + pageSize), total };
    }
}

export const MOCK_BRAND_TOTAL = MOCK_BRANDS.length;
